import { useEffect, useLayoutEffect, useRef } from 'react'

import { getChatroomMessages } from '../../../../api'
import { useApi } from '../../../../hooks'
import { getItemsFromApiResponse } from '../../../../utils/api'
import { useChannelSubscription } from '../../../Centrifuge'
import { CHATROOM_REALTIME_FETCH_DEBOUNCE_MS } from '../../Chatroom.constants'
import {
  getChatroomMessagesFromGetPrevResponse,
  getChatroomRealtimeChannel,
} from '../../Chatroom.utils'

import {
  getChatroomRealtimePayloadUuid,
  isChatroomRealtimeMessageCreated,
  isChatroomRealtimeMessageDeleted,
  isChatroomRealtimeMessageUpdated,
} from './useChatroomMessagesRealtime.utils'

export function useChatroomMessagesRealtime({
  chatroom,
  latestMessageUuidRef,
  messagesRef,
  outboundUuid,
  uuid,
  onMessageDeleted,
  onMessagesAppended,
  onMessageUpdated,
}) {
  const realtimeApi = useApi()
  const fetchTimeoutRef = useRef(null)

  // Callbacks captured in refs so the subscription effect can stay stable
  // and the in-flight fetch isn't aborted by parent re-renders.
  const onMessagesAppendedRef = useRef(onMessagesAppended)
  const onMessageDeletedRef = useRef(onMessageDeleted)
  const onMessageUpdatedRef = useRef(onMessageUpdated)

  useLayoutEffect(() => {
    onMessagesAppendedRef.current = onMessagesAppended
    onMessageDeletedRef.current = onMessageDeleted
    onMessageUpdatedRef.current = onMessageUpdated
  }, [onMessageDeleted, onMessagesAppended, onMessageUpdated])

  function fetchNewerMessages() {
    const fromUuid = latestMessageUuidRef.current
    const params = fromUuid
      ? { from: fromUuid, outboundUuid, uuid }
      : { getPrev: true, outboundUuid, uuid }

    realtimeApi.call(getChatroomMessages, params).then(response => {
      if (response) {
        const newer = fromUuid
          ? getItemsFromApiResponse(response)
          : getChatroomMessagesFromGetPrevResponse(response)

        if (newer.length > 0) {
          const seen = new Set(
            messagesRef.current.map(message => message?.uuid)
          )
          const fresh = newer.filter(
            message => message?.uuid && !seen.has(message.uuid)
          )

          if (fresh.length > 0) {
            onMessagesAppendedRef.current(fresh)
          }
        }
      }
    })
  }

  // Coalesce bursts of publications into a single fetch — the cursor grabs
  // everything newer either way, so N events = 1 request.
  function scheduleFetchNewerMessages() {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current)
    }

    fetchTimeoutRef.current = setTimeout(() => {
      fetchTimeoutRef.current = null
      fetchNewerMessages()
    }, CHATROOM_REALTIME_FETCH_DEBOUNCE_MS)
  }

  function handlePublication(ctx) {
    if (isChatroomRealtimeMessageCreated(ctx)) {
      scheduleFetchNewerMessages()
    } else if (isChatroomRealtimeMessageDeleted(ctx)) {
      const deletedUuid = getChatroomRealtimePayloadUuid(ctx)

      if (deletedUuid) {
        onMessageDeletedRef.current(deletedUuid)
      }
    } else if (isChatroomRealtimeMessageUpdated(ctx)) {
      const updatedUuid = getChatroomRealtimePayloadUuid(ctx)

      if (updatedUuid) {
        onMessageUpdatedRef.current(updatedUuid)
      }
    }
  }

  // Close any gap from a dropped WebSocket — every (re)subscribe pulls
  // everything newer than our last known message.
  function handleSubscribed() {
    fetchNewerMessages()
  }

  useChannelSubscription({
    channel: getChatroomRealtimeChannel(chatroom),
    onPublication: handlePublication,
    onSubscribed: handleSubscribed,
  })

  useEffect(() => {
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current)
        fetchTimeoutRef.current = null
      }

      realtimeApi.abort()
    }
  }, [realtimeApi, uuid])
}
